<template>
  <div class="markdown-editor" :class="{ 'markdown-editor--focused': focused }">
    <v-toolbar density="compact" color="surface" class="markdown-editor__toolbar">
      <v-btn-group density="compact" variant="text" divided>
        <v-btn size="small" @click="insertMarkdown('bold')" :title="t('md.bold')">
          <v-icon size="small">mdi-format-bold</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('italic')" :title="t('md.italic')">
          <v-icon size="small">mdi-format-italic</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('heading')" :title="t('md.heading')">
          <v-icon size="small">mdi-format-header-pound</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('code')" :title="t('md.inlineCode')">
          <v-icon size="small">mdi-code-tags</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('codeblock')" :title="t('md.codeBlock')">
          <v-icon size="small">mdi-code-braces</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('link')" :title="t('md.link')">
          <v-icon size="small">mdi-link-variant</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('ul')" :title="t('md.bulletList')">
          <v-icon size="small">mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('ol')" :title="t('md.numberedList')">
          <v-icon size="small">mdi-format-list-numbered</v-icon>
        </v-btn>
        <v-btn size="small" @click="insertMarkdown('quote')" :title="t('md.blockquote')">
          <v-icon size="small">mdi-format-quote-close</v-icon>
        </v-btn>
      </v-btn-group>

      <v-spacer />

      <v-btn-toggle v-model="viewMode" density="compact" mandatory variant="text">
        <v-btn value="edit" size="small">
          <v-icon size="small">mdi-pencil</v-icon>
        </v-btn>
        <v-btn value="split" size="small">
          <v-icon size="small">mdi-view-split-vertical</v-icon>
        </v-btn>
        <v-btn value="preview" size="small">
          <v-icon size="small">mdi-eye</v-icon>
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>

    <div class="markdown-editor__body" :class="`markdown-editor__body--${viewMode}`">
      <div v-show="viewMode !== 'preview'" class="markdown-editor__edit">
        <textarea
          ref="textareaRef"
          :value="modelValue"
          :placeholder="placeholder"
          class="markdown-editor__textarea"
          @input="$emit('update:modelValue', $event.target.value)"
          @focus="focused = true"
          @blur="focused = false"
          @keydown="handleKeydown"
        />
      </div>

      <div v-show="viewMode !== 'edit'" class="markdown-editor__preview">
        <div
          v-if="modelValue"
          class="markdown-body"
          v-html="renderedHtml"
        />
        <div v-else class="text-medium-emphasis text-body-2 pa-2">
          {{ t('md.nothingToPreview') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed } from 'vue'
import { useMarkdown } from '../../composables/useMarkdown.js'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '# My Skill\n\nWhen asked to perform this task...' },
})

const emit = defineEmits(['update:modelValue'])

const { render } = useMarkdown()
const textareaRef = ref(null)
const viewMode = ref('split')
const focused = ref(false)

const renderedHtml = computed(() => render(props.modelValue))

function getSelection() {
  const el = textareaRef.value
  return {
    start: el.selectionStart,
    end: el.selectionEnd,
    text: props.modelValue.slice(el.selectionStart, el.selectionEnd),
  }
}

function replaceSelection(before, after, defaultText = '') {
  const el = textareaRef.value
  const { start, end, text } = getSelection()
  const selected = text || defaultText
  const newValue =
    props.modelValue.slice(0, start) +
    before + selected + after +
    props.modelValue.slice(end)
  emit('update:modelValue', newValue)

  requestAnimationFrame(() => {
    el.focus()
    if (text) {
      el.setSelectionRange(start + before.length, start + before.length + selected.length)
    } else {
      el.setSelectionRange(start + before.length, start + before.length + defaultText.length)
    }
  })
}

function insertAtLineStart(prefix) {
  const el = textareaRef.value
  const { start } = getSelection()
  const val = props.modelValue
  const lineStart = val.lastIndexOf('\n', start - 1) + 1
  const newValue = val.slice(0, lineStart) + prefix + val.slice(lineStart)
  emit('update:modelValue', newValue)
  requestAnimationFrame(() => {
    el.focus()
    el.setSelectionRange(start + prefix.length, start + prefix.length)
  })
}

function insertMarkdown(type) {
  switch (type) {
    case 'bold': replaceSelection('**', '**', 'bold text'); break
    case 'italic': replaceSelection('_', '_', 'italic text'); break
    case 'heading': insertAtLineStart('## '); break
    case 'code': replaceSelection('`', '`', 'code'); break
    case 'codeblock': replaceSelection('```\n', '\n```', 'code here'); break
    case 'link': replaceSelection('[', '](url)', 'link text'); break
    case 'ul': insertAtLineStart('- '); break
    case 'ol': insertAtLineStart('1. '); break
    case 'quote': insertAtLineStart('> '); break
  }
}

function handleKeydown(e) {
  if (e.key === 'Tab') {
    e.preventDefault()
    replaceSelection('  ', '', '')
  }
}
</script>

<style scoped>
.markdown-editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.markdown-editor--focused {
  border-color: rgb(var(--v-theme-primary));
}

.markdown-editor__toolbar {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  flex-shrink: 0;
}

.markdown-editor__body {
  display: flex;
  flex: 1;
  min-height: 300px;
  max-height: 60vh;
}

.markdown-editor__body--edit .markdown-editor__edit { flex: 1; }
.markdown-editor__body--preview .markdown-editor__preview { flex: 1; }
.markdown-editor__body--split .markdown-editor__edit,
.markdown-editor__body--split .markdown-editor__preview {
  flex: 1;
  min-width: 0;
}
.markdown-editor__body--split .markdown-editor__preview {
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.markdown-editor__edit { display: flex; }

.markdown-editor__textarea {
  width: 100%;
  height: 100%;
  min-height: 300px;
  padding: 12px;
  border: none;
  outline: none;
  resize: none;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  background: transparent;
  color: inherit;
  tab-size: 2;
}

.markdown-editor__preview {
  overflow-y: auto;
  padding: 12px;
}

.markdown-body {
  font-size: 0.875rem;
  line-height: 1.6;
}
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3) { margin-top: 1em; margin-bottom: 0.5em; }
.markdown-body :deep(h1:first-child),
.markdown-body :deep(h2:first-child),
.markdown-body :deep(h3:first-child) { margin-top: 0; }
.markdown-body :deep(p) { margin-bottom: 0.75em; }
.markdown-body :deep(code) {
  background: rgba(var(--v-border-color), 0.12);
  padding: 0.15em 0.4em;
  border-radius: 3px;
  font-size: 0.85em;
}
.markdown-body :deep(pre) {
  background: rgba(var(--v-border-color), 0.08);
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 0.75em;
}
.markdown-body :deep(pre code) { background: none; padding: 0; }
.markdown-body :deep(blockquote) {
  border-left: 3px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-left: 12px;
  margin-left: 0;
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.5em;
  margin-bottom: 0.75em;
}
.markdown-body :deep(a) { color: rgb(var(--v-theme-primary)); }
</style>
